import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { cx } from './cx';

/** Pixels per second — ~44px/s ≈ legacy 110s loop for 16 win-ticker items. */
export const SMOOTH_MARQUEE_DEFAULT_SPEED = 44;

export type SmoothMarqueeCopyId = 'a' | 'b';

export interface SmoothMarqueeProps {
  /** Render one copy of the scrolling content; called twice for seamless loop. */
  children: (copyId: SmoothMarqueeCopyId) => ReactNode;
  className?: string;
  trackClassName?: string;
  copyClassName?: string;
  speedPxPerSec?: number;
  pauseOnHover?: boolean;
  'aria-label'?: string;
}

/**
 * GPU-composited infinite marquee: measures one copy in px, animates translate3d
 * by exactly that width so the loop seam stays seamless (no -50% subpixel jitter).
 */
export default function SmoothMarquee({
  children,
  className,
  trackClassName,
  copyClassName,
  speedPxPerSec = SMOOTH_MARQUEE_DEFAULT_SPEED,
  pauseOnHover = false,
  'aria-label': ariaLabel,
}: SmoothMarqueeProps) {
  const copyRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState<{ shiftPx: number; durationSec: number } | null>(
    null,
  );

  useLayoutEffect(() => {
    const copy = copyRef.current;
    if (!copy) return;

    const measure = () => {
      const width = copy.offsetWidth;
      if (width <= 0) return;
      setMetrics({
        shiftPx: width,
        durationSec: Math.max(width / speedPxPerSec, 8),
      });
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(copy);
    return () => ro.disconnect();
    // ResizeObserver handles content/size changes; avoid `children` dep (new fn each render).
  }, [speedPxPerSec]);

  const trackStyle: CSSProperties | undefined = metrics
    ? ({
        '--marquee-shift': `${metrics.shiftPx}px`,
        '--marquee-duration': `${metrics.durationSec}s`,
      } as CSSProperties)
    : undefined;

  return (
    <div
      className={cx(
        'smooth-marquee overflow-hidden',
        pauseOnHover && 'smooth-marquee--pausable',
        className,
      )}
      aria-label={ariaLabel}
    >
      <div
        className={cx(
          'smooth-marquee__track flex w-max [backface-visibility:hidden] [transform:translateZ(0)]',
          metrics && 'smooth-marquee__track--active',
          trackClassName,
        )}
        style={trackStyle}
      >
        <div
          ref={copyRef}
          className={cx('smooth-marquee__copy flex shrink-0 items-center', copyClassName)}
        >
          {children('a')}
        </div>
        <div
          className={cx('smooth-marquee__copy flex shrink-0 items-center', copyClassName)}
          aria-hidden="true"
        >
          {children('b')}
        </div>
      </div>
    </div>
  );
}
